/**
 * Crawler user-agent tokens: one source for
 * - src/lib/seo/crawler-ua.ts (fixed x-default root redirect for crawlers, plan 2026-10-06 A-2),
 * - nginx/crawler-prefetch.conf (RSC prefetches of rendering crawlers answered with 204, A-4;
 *   scripts/crawler-prefetch-nginx.test.mjs keeps its token list identical to RENDERING_BOT_TOKENS),
 * - scripts/cloudflare-ai-crawler-report.mjs (its test keeps the crawler families in these groups).
 * Plain ESM JavaScript so that the node:test scripts import it without a TypeScript step.
 *
 * The edge middleware imports this module (src/middleware.ts -> src/lib/seo/crawler-ua.ts), so
 * it imports nothing, least of all Next internals. Which crawlers get blocking metadata in
 * <head> is decided in src/config/head-metadata-bots.mjs (Googlebot since owner decision
 * D1 = A on 2026-10-16). A token added here enters the nginx map, not that list.
 */

/**
 * Google Search crawler. Google-Extended is a robots.txt token without an HTTP user agent of
 * its own.
 */
export const GOOGLE_SEARCH_CRAWLER_TOKENS = Object.freeze(["Googlebot"]);

/**
 * GoogleOther is Google's generic crawler for research and product teams, not Search. It
 * renders and prefetches (bot report 2026-10-06), so it gets the prefetch guard and the
 * root redirect.
 */
export const GOOGLE_OTHER_CRAWLER_TOKENS = Object.freeze(["GoogleOther"]);

/** AI search indexers and fetchers triggered by a user's question. */
export const AI_SEARCH_AND_USER_FETCHER_TOKENS = Object.freeze([
	"OAI-SearchBot",
	"ChatGPT-User",
	"Claude-SearchBot",
	"Claude-User",
	"PerplexityBot",
	"Perplexity-User",
	"DuckAssistBot",
	"MistralAI-User",
	"meta-externalfetcher",
	"meta-webindexer",
]);

/**
 * AI training crawlers, welcome since decision E4 (Content-Signal ai-train=yes).
 * Applebot-Extended is a robots.txt token only; Applebot is in Next's default list.
 */
export const AI_TRAINING_CRAWLER_TOKENS = Object.freeze([
	"GPTBot",
	"ClaudeBot",
	"anthropic-ai",
	"CCBot",
	"meta-externalagent",
	"Bytespider",
	"Amazonbot",
]);

/**
 * All four groups above. nginx/crawler-prefetch.conf carries the same tokens in lowercase.
 */
export const RENDERING_BOT_TOKENS = Object.freeze([
	...GOOGLE_SEARCH_CRAWLER_TOKENS,
	...GOOGLE_OTHER_CRAWLER_TOKENS,
	...AI_SEARCH_AND_USER_FETCHER_TOKENS,
	...AI_TRAINING_CRAWLER_TOKENS,
]);

/** @param {readonly string[]} tokens */
function alternation(tokens) {
	return tokens.map((token) => token.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|");
}

/**
 * JavaScript mirror of the nginx map in nginx/crawler-prefetch.conf: a case-insensitive
 * substring match on any RENDERING_BOT_TOKENS entry. Tests use it to prove that no browser
 * or in-app browser matches.
 */
export const RENDERING_BOT_UA_RE = new RegExp(alternation(RENDERING_BOT_TOKENS), "i");

/** Search, AI and social crawlers that get the fixed x-default root redirect. */
export const ROOT_REDIRECT_CRAWLER_RE = new RegExp(
	[
		"[\\w-]+-Google",
		"Google-[\\w-]+",
		"bingbot",
		"slurp",
		"duckduckbot",
		"baiduspider",
		"yandex",
		"applebot",
		"facebookexternalhit",
		"twitterbot",
		"linkedinbot",
		alternation(RENDERING_BOT_TOKENS),
	].join("|"),
	"i",
);
